/**
 * GeeTest v4 CAPTCHA module for Browser SDK.
 *
 * Automatically injects the GeeTest v4 script and provides
 * captcha initialization & verification helpers.
 */

// --- Types ---

/** GeeTest v4 verification result — must be sent to backend. */
export interface CaptchaResult {
  lot_number: string;
  captcha_output: string;
  pass_token: string;
  gen_time: string;
}

/** Options for initializing GeeTest captcha. */
export interface CaptchaOptions {
  /** GeeTest captchaId (site key). */
  captchaId: string;
  /** Product mode — default "bind" (popup). */
  product?: "bind" | "float" | "popup" | "custom";
  /** Language — default "zh-cn". */
  language?: string;
  /**
   * Per-attempt init timeout in milliseconds. Default 15000.
   * Increase for slow networks (e.g. 25000). Backward compatible — existing
   * callers that omit it keep the original 15s behavior.
   */
  timeout?: number;
  /**
   * Max number of init attempts **including the first** (i.e. total attempts, not extra
   * retries). Default 1 = no retry (original behavior). Pass 2 to retry once on
   * failure/timeout. Backward compatible — callers that omit it keep no-retry.
   */
  retries?: number;
}

// --- Internal state ---

let captchaObj: any = null;
let initOpts: CaptchaOptions | null = null;

// Minimal window-like type for SSR safety
interface WindowLike {
  document?: Document;
  initGeetest4?: (config: any, callback: (obj: any) => void) => void;
}

declare const globalThis: WindowLike;

// --- Script injection ---

function injectScript(timeout = 10000): Promise<void> {
  if (globalThis.initGeetest4) return Promise.resolve();

  // Avoid duplicate injection
  const doc = typeof document !== "undefined" ? document : globalThis.document;
  if (!doc) return Promise.reject(new Error("GeeTest: no document available"));

  const existing = doc.querySelector('script[src*="geetest"]');
  if (existing && globalThis.initGeetest4) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const script = doc.createElement("script");
    script.src = "https://static.geetest.com/v4/gt4.js";
    script.async = true;
    const timer = setTimeout(() => reject(new Error("GeeTest SDK 加载超时")), timeout);
    script.onload = () => {
      clearTimeout(timer);
      resolve();
    };
    script.onerror = () => {
      clearTimeout(timer);
      reject(new Error("GeeTest SDK 加载失败，请检查网络"));
    };
    const target = doc.head || doc.getElementsByTagName("head")[0];
    if (target) {
      target.appendChild(script);
    } else {
      clearTimeout(timer);
      reject(new Error("GeeTest: cannot inject script — no <head> element"));
    }
  });
}

// --- Public API ---

/** Default per-attempt init timeout (ms) — kept at 15000 for backward compatibility. */
const DEFAULT_INIT_TIMEOUT = 15000;
/** Default number of init attempts (including the first) — 1 = no retry. */
const DEFAULT_INIT_ATTEMPTS = 1;

/** Turn a GeeTest onError payload into a readable ": <code/detail>" suffix. */
function formatErrorDetail(err: any): string {
  if (!err) return "";
  const parts: string[] = [];
  const code = err.code ?? err.error_code;
  if (code !== undefined && code !== null && code !== "") parts.push(String(code));
  const msg = err.msg ?? err.message ?? err.desc ?? err.error_message;
  if (msg) parts.push(String(msg));
  const detail = parts.join(" ");
  return detail ? "：" + detail : "";
}

/** Single init attempt: inject script (idempotent) then init Geetest with `timeout`. */
function initOnce(opts: CaptchaOptions, timeout: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("GeeTest 初始化超时")), timeout);

    try {
      globalThis.initGeetest4!(
        {
          captchaId: opts.captchaId,
          product: opts.product ?? "bind",
          language: opts.language ?? "zh-cn",
        },
        (obj: any) => {
          obj.onReady(() => {
            clearTimeout(timer);
            captchaObj = obj;
            resolve();
          });
          obj.onError((err?: any) => {
            clearTimeout(timer);
            captchaObj = null;
            reject(new Error("GeeTest 初始化失败" + formatErrorDetail(err)));
          });
        },
      );
    } catch (e) {
      clearTimeout(timer);
      reject(e);
    }
  });
}

/**
 * Initialize GeeTest captcha. Must be called before `verifyCaptcha()`.
 * Usually called automatically by `BrowserAuthClient` when `captchaId` is provided.
 *
 * Retries up to `retries` total attempts (default 1 = no retry) with a per-attempt
 * `timeout` (default 15000ms). Both are opt-in — omitting them preserves the original behavior.
 */
export async function initCaptcha(opts: CaptchaOptions): Promise<void> {
  initOpts = opts;

  const timeout = opts.timeout ?? DEFAULT_INIT_TIMEOUT;
  const attempts = Math.max(1, opts.retries ?? DEFAULT_INIT_ATTEMPTS);

  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      await injectScript();
      await initOnce(opts, timeout);
      return;
    } catch (e) {
      lastErr = e;
      captchaObj = null;
      // brief backoff before the next attempt (only if we will retry)
      if (i < attempts - 1) {
        await new Promise((r) => setTimeout(r, 300));
      }
    }
  }

  throw lastErr instanceof Error ? lastErr : new Error("GeeTest 初始化失败");
}

/**
 * Show GeeTest captcha and return the verification result.
 * Throws if the user closes the captcha or an error occurs.
 */
export async function verifyCaptcha(): Promise<CaptchaResult> {
  if (!captchaObj) {
    if (!initOpts) {
      throw new Error("GeeTest 未初始化，请先调用 initCaptcha()");
    }
    await initCaptcha(initOpts);
  }

  return new Promise<CaptchaResult>((resolve, reject) => {
    captchaObj.onSuccess(() => {
      const result = captchaObj.getValidate();
      resolve({
        lot_number: result.lot_number,
        captcha_output: result.captcha_output,
        pass_token: result.pass_token,
        gen_time: result.gen_time,
      });
    });
    captchaObj.onClose(() => {
      reject(new Error("请完成图形验证"));
    });
    captchaObj.onError(() => {
      reject(new Error("图形验证出错"));
    });
    captchaObj.showCaptcha();
  });
}
